import { useState, useCallback, useMemo } from 'react';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, TextField,
} from '@mui/material';
import {
  Close as CloseIcon,
  MailOutline as UnreadIcon,
  Email as ReadIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { api } from '../../api/rpc';
import { queryKeys } from '../../api/queryKeys';
import { gwtStyles } from '../../config/theme';
import { pushError } from '../../stores/notificationStore';
import type { Message } from '../../api/types';

const columns: GridColDef[] = [
  {
    field: 'read',
    headerName: '',
    width: 32,
    renderCell: (params) =>
      params.value ? (
        <ReadIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
      ) : (
        <UnreadIcon sx={{ fontSize: 14, color: 'primary.main' }} />
      ),
  },
  { field: 'from', headerName: 'From', width: 130 },
  { field: 'subject', headerName: 'Subject', flex: 1, minWidth: 200 },
  { field: 'date', headerName: 'Date', width: 160 },
  {
    field: 'urgent',
    headerName: '!',
    width: 30,
    renderCell: (params) =>
      params.value ? (
        <Typography sx={{ color: 'error.main', fontSize: 14, fontWeight: 700 }}>!</Typography>
      ) : null,
  },
];

/**
 * Messaging panel — MessageListPanel equivalent.
 * Inbox + compose + read message detail.
 */
export const MessagingPanel = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.messages.all,
    queryFn: () => api.getMessages(),
    staleTime: 15_000,
  });

  const messages = useMemo(() => {
    const all = data?.records ?? [];
    if (!search) return all;
    const s = search.toLowerCase();
    return all.filter(
      (m) =>
        (m.from ?? '').toLowerCase().includes(s) ||
        (m.subject ?? '').toLowerCase().includes(s) ||
        (m.body ?? '').toLowerCase().includes(s),
    );
  }, [data, search]);

  const unreadCount = useMemo(() => messages.filter((m) => !m.read).length, [messages]);

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.messages.all });
  }, [queryClient]);

  const handleRowClick = useCallback(
    (params: { row: unknown }) => {
      const msg = params.row as Message;
      setSelectedMessage(msg);
      // Mark as read
      if (!msg.read) {
        api.markRead(msg.objKey).then(refresh).catch((e) => pushError(`Failed to mark message as read: ${e instanceof Error ? e.message : String(e)}`));
      }
    },
    [refresh],
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: gwtStyles.tabContent }}>
      {/* Title bar */}
      <Box
        sx={{
          height: 32,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 600, color: gwtStyles.tabTitle }}>
          Messages {unreadCount > 0 && `(${unreadCount} unread)`}
        </Typography>
        <Box sx={{ position: 'absolute', right: 4, display: 'flex', gap: 0.25 }}>
          <IconButton size="small" onClick={() => setComposeOpen(true)} title="Compose" sx={{ width: 28, height: 28 }}>
            <SendIcon sx={{ fontSize: 14 }} />
          </IconButton>
          <IconButton size="small" onClick={refresh} title="Refresh" sx={{ width: 28, height: 28 }}>
            <RefreshIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Search */}
      <Box sx={{ height: 32, flexShrink: 0, display: 'flex', alignItems: 'center', px: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <TextField
          size="small"
          placeholder="Search messages..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, '& .MuiInputBase-root': { fontSize: 12, height: 26 } }}
        />
      </Box>

      {/* Split: list + detail */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Message list */}
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          <DataGrid
            rows={messages}
            columns={columns}
            getRowId={(row) => (row as Message).objKey}
            loading={isLoading}
            density="compact"
            disableColumnMenu
            rowHeight={24}
            columnHeaderHeight={28}
            onRowClick={handleRowClick}
            sx={{
              border: 'none',
              fontSize: 12,
              cursor: 'pointer',
              '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: gwtStyles.gridRowOdd },
              '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: gwtStyles.gridRowEven },
              '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(25,118,210,0.08)' },
            }}
          />
        </Box>

        {/* Message detail */}
        {selectedMessage && (
          <Box
            sx={{
              width: 350,
              flexShrink: 0,
              borderLeft: '1px solid',
              borderColor: 'divider',
              overflow: 'auto',
              p: 1.5,
              backgroundColor: gwtStyles.mainView,
            }}
          >
            <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
              {selectedMessage.subject ?? '(no subject)'}
            </Typography>
            <Typography variant="caption" sx={{ fontSize: 11, color: 'text.secondary', display: 'block' }}>
              From: {selectedMessage.from ?? '—'} &bull; {selectedMessage.date ?? ''}
            </Typography>
            <Box sx={{ mt: 1, borderTop: '1px solid', borderColor: 'divider', pt: 1 }}>
              <Typography
                variant="body2"
                component="pre"
                sx={{
                  fontSize: 12,
                  fontFamily: 'inherit',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  m: 0,
                  lineHeight: 1.5,
                }}
              >
                {selectedMessage.body ?? '(empty message)'}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Compose dialog */}
      {composeOpen && (
        <ComposeDialog
          open
          onClose={() => setComposeOpen(false)}
          onSent={() => { setComposeOpen(false); refresh(); }}
        />
      )}
    </Box>
  );
};

/* ── Compose Dialog ── */
interface ComposeDialogProps {
  open: boolean;
  onClose: () => void;
  onSent: () => void;
}

const ComposeDialog = ({ open, onClose, onSent }: ComposeDialogProps) => {
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = useCallback(async () => {
    if (!to || !subject) return;
    setSending(true);
    try {
      await api.sendMessage(to, subject, body);
      onSent();
    } catch (e) {
      pushError(`Failed to send message: ${e instanceof Error ? e.message : String(e)}`);
    }
    setSending(false);
  }, [to, subject, body, onSent]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 480,
          borderRadius: '6px',
          border: `1px solid ${gwtStyles.borderSubtle}`,
          backgroundColor: gwtStyles.popupBg,
        },
      }}
    >
      <DialogTitle sx={{ fontSize: 14, fontWeight: 600, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: gwtStyles.wizardDetail }}>
        Compose Message
        <IconButton size="small" onClick={onClose} sx={{ width: 24, height: 24 }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ py: 2 }}>
        <FieldRow label="To">
          <TextField size="small" fullWidth value={to} onChange={(e) => setTo(e.target.value)} sx={fsx} />
        </FieldRow>
        <FieldRow label="Subject">
          <TextField size="small" fullWidth value={subject} onChange={(e) => setSubject(e.target.value)} sx={fsx} />
        </FieldRow>
        <FieldRow label="Message">
          <TextField
            size="small" fullWidth multiline rows={6}
            value={body} onChange={(e) => setBody(e.target.value)}
            sx={fsx}
          />
        </FieldRow>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 1.5 }}>
        <Button size="small" onClick={onClose} sx={{ textTransform: 'none', fontSize: 12 }}>Cancel</Button>
        <Button
          size="small" variant="contained" onClick={handleSend}
          disabled={sending || !to || !subject}
          sx={{ textTransform: 'none', fontSize: 12 }}
        >
          {sending ? 'Sending...' : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const FieldRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.75, gap: 1 }}>
    <Typography variant="body2" sx={{ fontSize: 12, minWidth: 70, textAlign: 'right', flexShrink: 0, pt: 0.5 }}>{label}:</Typography>
    <Box sx={{ flex: 1 }}>{children}</Box>
  </Box>
);

const fsx = { '& .MuiInputBase-root': { fontSize: 12 } };
